import React, { ComponentType, PureComponent, Suspense } from 'react';
import { When } from 'react-if';
import { Route, RouteComponentProps, withRouter } from 'react-router-dom';
import { BaseProps } from '../components/page-view/types';
import { root } from '../constants';
import { fomatParentPath } from '../utils';
import { UniqueDialogs, UniqueSystemDialog } from './dialogs';
import './index.less';
import MultiPage from './multi-page';
import RoutePage from './page';

const getIsThemeRoute = (_props: any) => {
  const matchUrl = _props?.match?.url;
  const rootPath = root || '/';

  // 判断是否是特色主题调用的RoutePageView
  return matchUrl === rootPath && _props.weId === 'd9sm55_5v07gi_v1eqhx';
};

interface RoutePageViewProps extends React.Attributes, BaseProps {
  showSystemDialog?: boolean;
  // 指定某个页面类型
  pageType?: string;
  // 指定某个页面id
  pageId?: string;
  // 是否排除设置菜单类型
  excludeSetting?: boolean;
  // 外部传入route组件的path
  routePath?: string;
  isRouteMain?: boolean;
  // 是否渲染页面
  isRenderPage?: boolean;
  // 返回当前是否匹配到路由页面内容
  onMatchRoutePage?: (hasMatchRoutePage: boolean) => void;
}

interface RoutePageViewState {}
class RoutePageView extends PureComponent<
  RoutePageViewProps & RouteComponentProps<{ id: string }>,
  RoutePageViewState
> {
  getSystemDialogContent = (isHidden?: boolean) => {
    const { showSystemDialog = true } = this.props;

    const isThemeRoute = getIsThemeRoute(this.props);

    // 通过调度中心加载RouteMain组件时，UniqueSystemDialog放在Route内加载
    const systemDialogContent = isHidden || !showSystemDialog ? null : (
        <UniqueSystemDialog weId={`${this.props.weId || ''}_xobcpf`} isThemeRoute={isThemeRoute} />
    );

    return systemDialogContent;
  };

  getPagePath = () => {
    const { routePath } = this.props;
    const parentPath = routePath || fomatParentPath(this.props);
    const isAppPath = /\/ebdapp\/view\/\w+/.test(parentPath);
    if (isAppPath) {
      const currentPath = location?.pathname?.slice?.(parentPath.length);
      const pagePathMatched = currentPath.match(/\/page\/\w+/);
      if (pagePathMatched) {
        return `${parentPath}/:type?/:id?`;
      }
    }
    return parentPath;
  }

  getRoutePath = () => {
    const {
      pageType, pageId, isRouteMain, routePath, location, match,
    } = this.props;
    let parentPath = routePath || fomatParentPath(this.props);

    // 门户配置/sp/ebdpage/view/xxx 指定URL的菜单时会转换成/portal/ebdpage/view/xxx
    // 调度中心会直接加载RouteMain组件，导致组件内需要用到match时，path不对的问题（path为'/portal'）
    // 菜单配置自定义URL也会直接加载RouteMain组件，导致组件内需要用到match时，path不对的问题（path为'/mobile/cusapp/:appId'）
    // 此处特殊处理，拼接完整的path
    const needCompletePath = isRouteMain
      && (['/portal', '/', root].includes(match.path)
        || ['/mobile/cusapp/:appId'].includes(match.path))
      && location.pathname.includes('/ebdpage/view');
    if (needCompletePath) {
      return `${parentPath}/ebdpage/view/:appId(\\d+_)?:pageId/:type?/:id?`;
    }

    /**
     * 菜单配置自定义URL直接加载RouteMain组件的情况补充
     * - path 中 :appId 为实际应用ID
     */
    const isCusappMobile = isRouteMain
      && /^\/mobile\/cusapp\/\d+$/.test(match.path)
      && location.pathname.includes('/ebdpage/view');

    if (isCusappMobile) {
      return `${parentPath}/ebdpage/view/:pageId`;
    }

    // 针对EB建模引入路由自动获取不正确时的情况，将当前路径作为path
    if (match.path && match.path.endsWith('/ebdfpage/')) {
      const { pathname } = location || {};

      // 处理移动端打开富文本编辑器弹框时页面无反应的问题
      if (/\/richtext_ckeditor_\d+_[\dA-Za-z]+$/.test(pathname)) {
        const [routePathName] = pathname.split('/richtext_ckeditor_') || [];

        if (routePathName) {
          return routePathName;
        }
      }

      return location.pathname;
    }

    const mainMenuPath = isRouteMain
      && location.pathname.includes('/ebdapp/')
      && location.pathname.includes('/ebdpage/view');
    if (mainMenuPath) {
      parentPath = location.pathname.match(/.*\/ebdpage\/view\/[\w-]*/)?.[0] || parentPath;
    }
    // eb弹窗事件改造后不再以history.push追加路由的方式打开
    // 导致外部模块无法匹配${parentPath}/:type/:id，从而打不开弹窗，故追加?兼容
    if (pageType && pageId) return parentPath;

    // 避免浏览按钮弹框会匹配上ID（workflow_WorkflowViewDialog）
    const pathReg = new RegExp(
      `${parentPath}/([0-9a-zA-Z-_]*)/([0-9a-zA-Z-_]*)`.replace(/\/\//g, '/'),
    );
    const matched = pathReg?.exec(location.pathname);
    const browserDlgReg = /dialog/i;
    if (matched && browserDlgReg.test(matched?.[1])) {
      return parentPath;
    }
    if (matched && browserDlgReg.test(matched?.[2])) {
      return `${parentPath}/:type?`;
    }
    return `${parentPath}/:type?/:id?`;
  };

  renderComponent = () => {
    const { isRouteMain, isRenderPage = true } = this.props;

    const path = this.getRoutePath();

    // 通过调度中心加载RouteMain组件时，UniqueSystemDialog放在Route内加载
    const systemDialogContent = this.getSystemDialogContent(!isRouteMain);

    const isThemeRoute = getIsThemeRoute(this.props);

    return (
      <Route weId={`${this.props.weId || ''}_b3sv49`} path={path}>
        <Suspense weId={`${this.props.weId || ''}_46wfmh`} fallback={<div />}>
          {/* page、workflow、form等 */}
          <When weId={`${this.props.weId || ''}_vrwgeo`} condition={isRenderPage}>
            <RoutePage weId={`${this.props.weId || ''}_pkd0lb`} {...this.props} enabledMultiPage />
          </When>
          <UniqueDialogs weId={`${this.props.weId || ''}_u3h22i`} isThemeRoute={isThemeRoute} />
          {systemDialogContent}
        </Suspense>
      </Route>
    );
  };

  render() {
    const { isRouteMain, isRenderPage = true } = this.props;

    const path = this.getRoutePath();

    const pagePath = this.getPagePath();

    // 通过调度中心加载RouteMain组件时，UniqueSystemDialog放在Route内加载
    const systemDialogContent = this.getSystemDialogContent(isRouteMain);

    return (
      <>
        {/**
         * 1. 多页面支持，页面实际渲染的ReactNode
         * 2. 页面需要放到最上面，避免其他弹窗后生成的空节点(<div class='xxx'></div>)影响样式(如页面预览)
         */}
        <When weId={`${this.props.weId || ''}_vrwgeo`} condition={isRenderPage}>
          <Route
            weId={`${this.props.weId || ''}_4ya8zr`}
            path={pagePath}
            component={MultiPage}
          />
        </When>

        {/** 多页面改造后，改路由用于监听路由变化，不含实际渲染的节点，实际渲染的页面节点在MutliPage处理 */}
        <Route
          weId={`${this.props.weId || ''}_b7df88`}
          // eb弹窗事件改造后不再以history.push追加路由的方式打开
          // 导致外部模块无法匹配${parentPath}/:type/:id，从而打不开弹窗，故追加?兼容
          path={path}
          render={this.renderComponent}
        />

        {/* 弹框 门户/portal弹框不再放在页面内引入，统一在RoutePageView中引入 */}
        {systemDialogContent}
      </>
    );
  }
}

export default withRouter(RoutePageView as any) as unknown as ComponentType<RoutePageViewProps>;
